import { Trans, useTranslation } from 'react-i18next';
import { BenefitIcon } from '@/web/components/BenefitIcon/BenefitIcon';
import { LearningCentreModal } from '@/web/components/LearningCentreModal/LearningCentreModal';
import { IconCoverflex } from '@coverflex-tech/hypernova';
import classNames from 'classnames';
import css from './savings-learning-centre.module.css';

interface IProps {
  onClose: () => void;
}

const SavingExampleRow = ({
  valueKey,
  labelKey,
  className,
}: {
  valueKey: string;
  labelKey: string;
  className?: string;
}) => (
  <div className={classNames(css.row, className)}>
    <div className={css.column}>
      <Trans i18nKey={labelKey} />
    </div>
    <Trans i18nKey={valueKey} />
  </div>
);

const SavingsLearningCentre = ({ onClose }: IProps) => {
  const { t } = useTranslation();
  return (
    <LearningCentreModal
      title={t('learning_centre.savings.title')}
      subtitle={t('learning_centre.savings.subtitle')}
      icon={
        <IconCoverflex
          width={21}
          className={classNames(css.circleIcon, css.blueIcon)}
        />
      }
      context="savings"
      steps={[
        <>
          <div className={css.learningCentreTitle}>
            {t('learning_centre.savings.step1.title')}
          </div>
          <Trans i18nKey="learning_centre.savings.step1.description" />
        </>,
        <>
          <div className={css.learningCentreTitle}>
            <BenefitIcon
              className={classNames(css.blueIcon, css.learningCentreIcon)}
              benefit="health-wellbeing"
            />
            <span>{t('learning_centre.savings.step2.title')}</span>
          </div>
          <div className={classNames(css.learningCentreContentRow, css.row)}>
            <div className={css.column}>
              <Trans i18nKey="learning_centre.savings.step2.description" />
            </div>
            <div className={css.column}>
              <SavingExampleRow
                labelKey="learning_centre.savings.step2.liquid"
                valueKey="learning_centre.savings.step2.liquidAmount"
                className={css.marginBottomMd}
              />
              <SavingExampleRow
                labelKey="learning_centre.savings.step2.gross"
                valueKey="learning_centre.savings.step2.grossAmount"
              />
              <SavingExampleRow
                labelKey="learning_centre.savings.step2.tax"
                valueKey="learning_centre.savings.step2.taxAmount"
              />
              <div className={css.dashedLine} />
              <SavingExampleRow
                className={css.bold}
                labelKey="learning_centre.savings.step2.saving"
                valueKey="learning_centre.savings.step2.savingAmount"
              />
            </div>
          </div>
        </>,
        <>
          <div className={css.learningCentreTitle}>
            <BenefitIcon
              className={classNames(css.blueIcon, css.learningCentreIcon)}
              benefit="childcare-voucher"
            />
            {t('learning_centre.savings.step3.title')}
          </div>
          <div className={classNames(css.learningCentreContentRow, css.row)}>
            <div className={css.column}>
              <Trans i18nKey="learning_centre.savings.step3.description" />
            </div>
            <div className={css.column}>
              <SavingExampleRow
                labelKey="learning_centre.savings.step3.liquid"
                valueKey="learning_centre.savings.step3.liquidAmount"
                className={css.marginBottomMd}
              />
              <SavingExampleRow
                labelKey="learning_centre.savings.step3.gross"
                valueKey="learning_centre.savings.step3.grossAmount"
              />
              <SavingExampleRow
                labelKey="learning_centre.savings.step3.tax"
                valueKey="learning_centre.savings.step3.taxAmount"
              />
              <SavingExampleRow
                labelKey="learning_centre.savings.step3.pit"
                valueKey="learning_centre.savings.step3.pitAmount"
              />
              <div className={css.dashedLine} />
              <SavingExampleRow
                className={css.bold}
                labelKey="learning_centre.savings.step3.saving"
                valueKey="learning_centre.savings.step3.savingAmount"
              />
            </div>
          </div>
        </>,
      ]}
      onClose={onClose}
    />
  );
};

export default SavingsLearningCentre;
